import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  HttpStatusCode,
  InternalAxiosRequestConfig,
} from 'axios';
import { ResponseError } from '../../entities/ResponseError';
import { ExceptionEnum } from '../../enums/ExceptionEnum';
import { errorsCode } from '../../utils/consts/errorsCode';

interface IApiErrorPayload {
  code: keyof typeof errorsCode;
  name: ExceptionEnum;
  statusCode: number;
  message: string;
  timestamp?: string;
  details?: unknown;
}

export interface IHttpClientConfig {
  baseURL: string;
  withCredentials?: boolean;
  getAuthHeaders?: () =>
    | Promise<Record<string, string> | undefined>
    | Record<string, string>
    | undefined;
  refreshSession: () => Promise<void>;
  onForceLogout: () => void;
}

export function isIApiErrorPayload(value: unknown): value is IApiErrorPayload {
  return (
    typeof value === 'object' &&
    value !== null &&
    'code' in value &&
    'name' in value &&
    'statusCode' in value &&
    'message' in value
  );
}

export function toResponseError(payload: IApiErrorPayload): ResponseError {
  return new ResponseError(
    payload.code,
    payload.message,
    payload.name,
    payload.statusCode,
    payload.timestamp ? new Date(payload.timestamp) : new Date(),
    payload.details,
  );
}

export const httpClient: AxiosInstance = axios.create({
  timeout: 10000,
});

let isRefreshing = false;
let failedQueue: {
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
  config: AxiosRequestConfig;
}[] = [];

function processQueue(error: AxiosError | null) {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    } else {
      resolve(httpClient(config));
    }
  });
  failedQueue = [];
}

function isJsonResponse(response: { headers?: Record<string, unknown> }) {
  const contentType = response.headers?.['content-type'];
  return (
    typeof contentType === 'string' && contentType.includes('application/json')
  );
}

function hasEmptyBody(response: { data?: unknown }) {
  return (
    response.data === null ||
    response.data === undefined ||
    response.data === ''
  );
}

function isBinaryRequest(response: {
  config?: { responseType?: string };
}): boolean {
  const responseType = response.config?.responseType;
  return responseType === 'blob' || responseType === 'arraybuffer';
}

let requestInterceptorId: number | null = null;
let responseInterceptorId: number | null = null;

export function setupHttpClient(config: IHttpClientConfig): void {
  httpClient.defaults.baseURL = config.baseURL;
  httpClient.defaults.withCredentials = config.withCredentials ?? false;

  if (requestInterceptorId !== null) {
    httpClient.interceptors.request.eject(requestInterceptorId);
    requestInterceptorId = null;
  }
  if (responseInterceptorId !== null) {
    httpClient.interceptors.response.eject(responseInterceptorId);
    responseInterceptorId = null;
  }

  const { getAuthHeaders } = config;
  if (getAuthHeaders) {
    requestInterceptorId = httpClient.interceptors.request.use(
      async (request: InternalAxiosRequestConfig) => {
        const headers = await getAuthHeaders();
        if (headers) {
          Object.entries(headers).forEach(([key, value]) => {
            request.headers.set(key, value);
          });
        }
        return request;
      },
    );
  }

  responseInterceptorId = httpClient.interceptors.response.use(
    (response) => {
      if (
        response.status !== HttpStatusCode.NoContent &&
        !hasEmptyBody(response) &&
        !isJsonResponse(response) &&
        !isBinaryRequest(response)
      ) {
        return Promise.reject(
          new ResponseError(
            'global',
            'Unexpected response format',
            ExceptionEnum.INTERNAL_SERVER_ERROR,
            response.status,
            new Date(),
            {
              contentType: response.headers?.['content-type'],
              url: response.config?.url,
            },
          ),
        );
      }

      return response.data;
    },
    async (error) => {
      const originalRequest = error.config;
      const payload = error.response?.data;

      if (
        isIApiErrorPayload(payload) &&
        [
          ExceptionEnum.TENANT_FORBIDDEN_ACCESS,
          ExceptionEnum.TENANT_IS_INACTIVE,
        ].includes(payload.name)
      ) {
        config.onForceLogout();
        return Promise.reject(toResponseError(payload));
      }

      const isUnauthorized =
        error.response?.status === HttpStatusCode.Unauthorized;
      const isAuthRoute =
        originalRequest?.url === 'auth/signin' ||
        originalRequest?.url === 'auth/refresh-session';

      if (isUnauthorized && !isAuthRoute && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject, config: originalRequest });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          await config.refreshSession();

          processQueue(null);
          return httpClient(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError as AxiosError);
          config.onForceLogout();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      if (isUnauthorized && !isAuthRoute) {
        config.onForceLogout();
      }

      if (isIApiErrorPayload(payload)) {
        return Promise.reject(toResponseError(payload));
      }

      return Promise.reject(error);
    },
  );
}
